import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Skeleton } from "@heroui/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowLeft,
    faDrawPolygon,
    faSave,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { Feature, FeatureCollection, MultiPolygon, Polygon } from "geojson";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useRequest from "@/lib/request/useRequest";
import useMap from "@/lib/map/useMap";
import { polygonsOverlap } from "@/lib/utils/map";
import { DEFAULT_CROP_ZONE_COLOR } from "@/types/cropZone";
import { useCropZoneHighlight } from "@/components/field/setup/CropZoneHighlight";
import CropZoneColorPicker from "@/components/field/setup/CropZoneColorPicker";

function polygonsFromGeometry(
    geometry: Polygon | MultiPolygon,
): FeatureCollection<Polygon> {
    if (geometry.type === "Polygon") {
        return {
            type: "FeatureCollection",
            features: [
                {
                    type: "Feature",
                    properties: {},
                    geometry,
                },
            ],
        };
    }
    return {
        type: "FeatureCollection",
        features: geometry.coordinates.map((coordinates) => ({
            type: "Feature" as const,
            properties: {},
            geometry: {
                type: "Polygon" as const,
                coordinates,
            },
        })),
    };
}

function featuresToMultiPolygon(features: Feature[]): MultiPolygon | null {
    const coordinates = features
        .filter(
            (feature): feature is Feature<Polygon> =>
                feature.geometry?.type === "Polygon",
        )
        .map((feature) => feature.geometry.coordinates)
        .filter((rings) => rings.length > 0);
    if (!coordinates.length) return null;
    return { type: "MultiPolygon", coordinates };
}

function CropZoneEditor({ cropZoneId }: { cropZoneId: string }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { useMutateRequest } = useRequest();
    const { map, mapLoaded } = useMap();
    const { setEditingZoneId } = useCropZoneHighlight();
    const { fieldId, fieldQuery, geometryQuery } = useFieldDashboard() ?? {};
    const draw = useRef<MapboxDraw | null>(null);
    const drawnBoundary = useRef<MultiPolygon | null>(null);

    const cropZone = useMemo(() => {
        return fieldQuery?.data?.crop_zones?.find(
            (zone) => String(zone.id) === String(cropZoneId),
        );
    }, [fieldQuery?.data?.crop_zones, cropZoneId]);

    const zoneGeometry = useMemo(() => {
        const feature = geometryQuery?.data?.crop_zones?.features?.find(
            (item) =>
                String(item.id ?? item.properties?.id) === String(cropZoneId),
        );
        return feature?.geometry ?? null;
    }, [geometryQuery?.data?.crop_zones, cropZoneId]);

    const otherZoneFeatures = useMemo(() => {
        return (geometryQuery?.data?.crop_zones?.features ?? []).filter(
            (item) =>
                String(item.id ?? item.properties?.id) !== String(cropZoneId),
        );
    }, [geometryQuery?.data?.crop_zones, cropZoneId]);

    const [name, setName] = useState("");
    const [color, setColor] = useState(DEFAULT_CROP_ZONE_COLOR);
    const [isEditingBoundary, setIsEditingBoundary] = useState(false);
    const [editGeometry, setEditGeometry] = useState<
        Polygon | MultiPolygon | null
    >(null);
    const [hasDrawnBoundary, setHasDrawnBoundary] = useState(false);
    const [boundaryOverlaps, setBoundaryOverlaps] = useState(false);

    useEffect(() => {
        if (!cropZone) return;
        setName(cropZone.name);
        setColor(cropZone.color || DEFAULT_CROP_ZONE_COLOR);
    }, [cropZone]);

    useEffect(() => {
        return () => setEditingZoneId(null);
    }, [setEditingZoneId]);

    const saveCropZone = useMutateRequest({
        successMessage: t("success.crop_zone_saved_successfully", {
            ns: "messages",
        }),
        errorMessage: t("error.failed_to_save_crop_zone", {
            ns: "messages",
        }),
    });

    const goBack = useCallback(() => {
        if (!fieldId) return;
        navigate({
            to: "/dashboard/fields/$fieldId/setup/crop-zones",
            params: { fieldId: String(fieldId) },
        });
    }, [fieldId, navigate]);

    useEffect(() => {
        if (fieldQuery?.isLoading || !fieldQuery?.data) return;
        if (!cropZone) {
            goBack();
        }
    }, [fieldQuery?.isLoading, fieldQuery?.data, cropZone, goBack]);

    const syncDrawnBoundary = useCallback(() => {
        const boundary = featuresToMultiPolygon(
            draw.current?.getAll().features ?? [],
        );
        drawnBoundary.current = boundary;
        setHasDrawnBoundary(!!boundary);
        setBoundaryOverlaps(
            !!boundary &&
                otherZoneFeatures.some((feature) =>
                    polygonsOverlap(
                        {
                            type: "Feature",
                            properties: {},
                            geometry: boundary,
                        },
                        feature,
                    ),
                ),
        );
    }, [otherZoneFeatures]);

    useEffect(() => {
        if (!isEditingBoundary || !editGeometry || !map || !mapLoaded) return;

        const drawControl = new MapboxDraw({
            displayControlsDefault: false,
            controls: {
                polygon: true,
                trash: true,
            },
        });
        draw.current = drawControl;
        map.addControl(drawControl, "top-left");

        const ids = drawControl.add(polygonsFromGeometry(editGeometry));
        if (ids[0]) {
            drawControl.changeMode("direct_select", { featureId: ids[0] });
        }
        syncDrawnBoundary();

        map.on("draw.create", syncDrawnBoundary);
        map.on("draw.update", syncDrawnBoundary);
        map.on("draw.delete", syncDrawnBoundary);

        return () => {
            map.off("draw.create", syncDrawnBoundary);
            map.off("draw.update", syncDrawnBoundary);
            map.off("draw.delete", syncDrawnBoundary);
            if (map.hasControl(drawControl)) {
                map.removeControl(drawControl);
            }
            draw.current = null;
            map.getCanvas().style.cursor = "";
        };
    }, [editGeometry, isEditingBoundary, map, mapLoaded, syncDrawnBoundary]);

    const onEditBoundary = useCallback(() => {
        if (isEditingBoundary || !zoneGeometry || !cropZone) return;
        setEditGeometry(zoneGeometry);
        setIsEditingBoundary(true);
        setHasDrawnBoundary(false);
        setEditingZoneId(cropZone.id);
    }, [cropZone, isEditingBoundary, setEditingZoneId, zoneGeometry]);

    const onSave = useCallback(() => {
        if (!cropZone) return;
        const boundary = isEditingBoundary ? drawnBoundary.current : null;
        if (isEditingBoundary && (!boundary || boundaryOverlaps)) return;
        saveCropZone.mutate(
            {
                url: `crop_zones/${cropZone.id}/`,
                method: "patch",
                data: {
                    name: name.trim(),
                    color,
                    ...(boundary ? { boundary } : {}),
                },
            },
            {
                onSuccess: () => {
                    fieldQuery?.refetch();
                    geometryQuery?.refetch();
                    goBack();
                },
            },
        );
    }, [
        cropZone,
        name,
        color,
        isEditingBoundary,
        boundaryOverlaps,
        saveCropZone,
        fieldQuery,
        geometryQuery,
        goBack,
    ]);

    if (!fieldId || fieldQuery?.isLoading || !cropZone) {
        return (
            <div className="flex flex-col gap-2 w-full">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3 w-full">
            <div className="flex flex-row gap-2 items-center">
                <Button
                    size="sm"
                    variant="light"
                    isIconOnly
                    as={Link}
                    to="/dashboard/fields/$fieldId/setup/crop-zones"
                    params={
                        {
                            fieldId: String(fieldId),
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        } as any
                    }
                    aria-label={t("crop_zones.back", { ns: "setup" })}
                >
                    <FontAwesomeIcon icon={faArrowLeft} />
                </Button>
                <p className="text-md font-semibold">
                    {t("crop_zones.edit_crop_zone", { ns: "setup" })}
                </p>
            </div>
            <div className="flex flex-row gap-2 items-end">
                <CropZoneColorPicker
                    value={color}
                    onChange={setColor}
                    ariaLabel={t("crop_zones.color", { ns: "setup" })}
                />
                <Input
                    size="sm"
                    label={t("crop_zones.name", { ns: "setup" })}
                    value={name}
                    onValueChange={setName}
                    labelPlacement="outside"
                />
            </div>
            <Button
                size="sm"
                variant={isEditingBoundary ? "solid" : "bordered"}
                color={isEditingBoundary ? "primary" : "default"}
                startContent={<FontAwesomeIcon icon={faDrawPolygon} />}
                isDisabled={!zoneGeometry}
                onPress={onEditBoundary}
            >
                {t("crop_zones.edit_boundary", { ns: "setup" })}
            </Button>
            {isEditingBoundary && boundaryOverlaps && (
                <p className="text-sm text-danger">
                    {t("crop_zones.boundary_overlap_error", { ns: "setup" })}
                </p>
            )}
            <div className="flex flex-row gap-2 justify-end">
                <Button
                    size="sm"
                    variant="light"
                    as={Link}
                    to="/dashboard/fields/$fieldId/setup/crop-zones"
                    params={
                        {
                            fieldId: String(fieldId),
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        } as any
                    }
                >
                    {t("crop_zones.cancel", { ns: "setup" })}
                </Button>
                <Button
                    size="sm"
                    color="primary"
                    startContent={<FontAwesomeIcon icon={faSave} />}
                    isLoading={saveCropZone.isPending}
                    isDisabled={
                        !name.trim() ||
                        saveCropZone.isPending ||
                        (isEditingBoundary &&
                            (!hasDrawnBoundary || boundaryOverlaps))
                    }
                    onPress={onSave}
                >
                    {t("crop_zones.save", { ns: "setup" })}
                </Button>
            </div>
        </div>
    );
}

export default CropZoneEditor;
